import { useEffect, useState } from 'react';

import { formatFileSize, getAssetType } from '../lib/assetUtils';
import { AssetModal } from './AssetModal';

export type AssetPreviewModel = {
  id: string;
  url: string;
  originalName: string;
  mimeType: string;
  size: number;
};

type Props = {
  asset: AssetPreviewModel;
  resolveUrl?: () => Promise<string>;
};

export function AssetPreview({ asset, resolveUrl }: Props) {
  const [resolvedUrl, setResolvedUrl] = useState(asset.url);
  const [modalUrl, setModalUrl] = useState<string | null>(null);
  const type = getAssetType(asset.mimeType, asset.originalName);

  useEffect(() => {
    setResolvedUrl(asset.url);
  }, [asset.id, asset.url]);

  useEffect(() => {
    if (type !== 'image') return;
    const existing = resolvedUrl || asset.url;
    if (existing || !resolveUrl) return;

    let cancelled = false;
    void resolveUrl().then((url) => {
      if (!cancelled && url) setResolvedUrl(url);
    });

    return () => {
      cancelled = true;
    };
  }, [type, asset.id, asset.url, resolvedUrl, resolveUrl]);

  async function ensureUrl(): Promise<string> {
    if (resolvedUrl) return resolvedUrl;
    if (asset.url) {
      setResolvedUrl(asset.url);
      return asset.url;
    }
    if (resolveUrl) {
      const url = await resolveUrl();
      if (url) setResolvedUrl(url);
      return url;
    }
    return '';
  }

  async function openImageModal() {
    const url = await ensureUrl();
    if (!url) return;
    setModalUrl(url);
  }

  async function handleDownload(e: React.MouseEvent) {
    e.preventDefault();
    const url = await ensureUrl();
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = asset.originalName;
    link.rel = 'noopener';
    link.click();
  }

  if (type === 'image') {
    const thumbUrl = resolvedUrl || asset.url;
    return (
      <>
        <div
          className="asset-preview-image-wrap"
          onClick={() => void openImageModal()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') void openImageModal();
          }}
        >
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt={asset.originalName}
              className="asset-preview-image"
            />
          ) : (
            <span className="asset-preview-image-placeholder">🖼️ {asset.originalName}</span>
          )}
        </div>
        {modalUrl && (
          <AssetModal
            url={modalUrl}
            filename={asset.originalName}
            onClose={() => setModalUrl(null)}
          />
        )}
      </>
    );
  }

  const icon = type === 'pdf' ? '📄' : '📝';
  const typeLabel = type === 'pdf' ? 'PDF' : type === 'doc' ? 'Word Document' : 'File';

  return (
    <div className="asset-preview-card">
      <span className="asset-preview-icon" aria-hidden>
        {icon}
      </span>
      <div className="asset-preview-meta">
        <div className="asset-preview-name">{asset.originalName}</div>
        <div className="asset-preview-sub">
          {typeLabel} · {formatFileSize(asset.size)}
        </div>
      </div>
      <button
        type="button"
        className="asset-preview-download"
        title="Download"
        onClick={(e) => void handleDownload(e)}
      >
        ↓
      </button>
    </div>
  );
}
